import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowUpRight, ArrowLeft, HeartPulse, MessageCircle, Users, HandHeart,
  Activity, Wind, Tent, Fish, Dumbbell, GraduationCap, Handshake, Sparkles,
  Facebook, Instagram, Link as LinkIcon, Download,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import ExperienceWishlist from "@/components/ExperienceWishlist";
import useSEO from "@/hooks/useSEO";

const SAGE = "#b3475a";
const US_SHARE_URL = "https://tandemtfoundation.org/foundation/unspoken";

const pillars = [
  { icon: HeartPulse, title: "Men's Mental Health & Wellness", body: "Bringing awareness to the emotional and mental challenges men experience, and encouraging men to make their well-being a priority." },
  { icon: MessageCircle, title: "Creating Space for Men to Talk", body: "Environments where men can have honest conversations — without judgment, pressure, or the expectation to have everything figured out." },
  { icon: Users, title: "Supporting Men Beyond the Surface", body: "Looking beyond \u201cI'm good\u201d and recognizing the stress, responsibilities, fears, losses, and life experiences men may be carrying privately." },
  { icon: HandHeart, title: "Brotherhood & Support", body: "Building genuine connections between men and reminding them that they don't have to navigate every season of life alone." },
  { icon: Dumbbell, title: "Movement & Physical Wellness", body: "Using exercise, fitness, sports, fishing, and the outdoors as opportunities to decompress, connect, and support overall wellness." },
  { icon: Wind, title: "Peace of Mind", body: "Exploring what true peace and balance look like — learning how to slow down, reset, set boundaries, and create space to breathe." },
  { icon: Tent, title: "Retreats & Experiences", body: "Opportunities to step away from everyday pressures through retreats, outdoor experiences, reflection, and meaningful conversation." },
];

const conversations = [
  "Stress", "Fatherhood", "Relationships", "Financial pressure", "Grief & loss",
  "Burnout", "Loneliness", "Anger", "Fear", "Purpose", "Self-worth",
  "Life transitions", "The pressure to provide", "Always being strong", "What it means to be okay",
];

const waysToSupport = [
  { icon: Fish, title: "Sponsor an Experience", body: "Help fund an UNSPOKEN\u2122 retreat, fishing experience, workout, wellness day, or community conversation." },
  { icon: Handshake, title: "Become a Community Partner", body: "Gyms, barbershops, wellness & mental-health professionals, restaurants, and employers can help us reach more men." },
  { icon: Tent, title: "Provide an Experience", body: "Donate gym access, fishing trips, outdoor activities, meals, retreat space, or wellness services." },
  { icon: GraduationCap, title: "Share Your Expertise", body: "Counselors, coaches, fitness pros, financial educators, fathers, and mentors can bring conversations directly to men." },
  { icon: HandHeart, title: "Support the Mission", body: "Financial and in-kind contributions make UNSPOKEN\u2122 programming accessible to men who might never otherwise take part." },
  { icon: MessageCircle, title: "Start the Conversation", body: "Ask the men in your life, \u201cHow are you really doing?\u201d Listen without judgment. Encourage connection." },
];

const US_WISHLIST_CONFIG = {
  program: "UNSPOKEN",
  testidPrefix: "us",
  variant: "dark",
  accent: SAGE,
  eyebrow: "Support UNSPOKEN\u2122",
  titleLead: "Help us break the silence.",
  titleAccent: "Sponsor a moment.",
  blurb:
    "Supporting men's mental health starts with creating spaces where men feel comfortable showing up, connecting, and being themselves. Choose one experience to make possible.",
  dialogEyebrow: "Pledge Your Support",
  ctaVerb: "Sponsor This",
  submitVerb: "Make This Pledge",
  experiences: [
    { icon: Fish, name: "A Fishing Experience", line: "Open water, open conversation.", amount: "$120" },
    { icon: Dumbbell, name: "A Workout or Fitness Day", line: "Move the weight you carry.", amount: "$75" },
    { icon: Wind, name: "A Wellness Day", line: "Space to slow down and breathe.", amount: "$150" },
    { icon: Tent, name: "A Brotherhood Retreat", line: "Step away. Reset. Reconnect.", amount: "$500" },
    { icon: MessageCircle, name: "A Community Conversation", line: "One honest table changes everything.", amount: "$200" },
    { icon: GraduationCap, name: "Share Your Expertise", line: "Counselors, coaches & mentors welcome.", amount: "In-kind" },
    { icon: HandHeart, name: "Support the Mission", line: "Help a man just be.", amount: "Any" },
  ],
};

function Eyebrow({ children }) {
  return (
    <p className="text-xs sm:text-sm font-semibold tracking-[0.25em] uppercase" style={{ color: SAGE }}>
      {children}
    </p>
  );
}

export default function Unspoken() {
  useSEO({
    title: "UNSPOKEN — Men's Mental Health & Wellness | The Tandem T Foundation",
    description:
      "UNSPOKEN breaks the silence around men's mental health — creating space for men to connect, decompress, and prioritize their well-being through conversation, movement, brotherhood, and experiences.",
    path: "/foundation/unspoken",
  });

  const shareFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(US_SHARE_URL)}`, "_blank", "noopener,noreferrer,width=640,height=640");
  };
  const shareInstagram = async () => {
    try { await navigator.clipboard.writeText(US_SHARE_URL); toast.success("Link copied — paste it into your Instagram post or story."); }
    catch { toast.message("Copy this link for Instagram", { description: US_SHARE_URL }); }
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(US_SHARE_URL); toast.success("Link copied to clipboard."); }
    catch { toast.message("Copy this link", { description: US_SHARE_URL }); }
  };

  return (
    <div
      className="min-h-screen text-[#efe6e8] bg-gradient-to-b from-[#0b0708] via-[#25090f] to-[#120709]"
      data-testid="page-unspoken"
    >
      {/* HERO */}
      <section className="relative overflow-hidden pt-36 pb-24 md:pt-44 md:pb-32" data-testid="us-hero">
        <div className="absolute inset-0" aria-hidden="true">
          <img
            src="https://images.unsplash.com/photo-1528164604878-28ea0fb4f462?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b0708]/85 via-[#25090f]/80 to-[#120709]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b0708]/90 via-transparent to-transparent" />
        </div>
        <div className="absolute -top-32 right-[-10%] w-[42rem] h-[42rem] rounded-full bg-[#7a2231]/25 blur-3xl" aria-hidden="true" />
        <div className="absolute bottom-[-20%] left-[-8%] w-[36rem] h-[36rem] rounded-full bg-[#3a1016]/40 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal>
            <Eyebrow>The Tandem T Foundation</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h1
              data-testid="us-wordmark"
              className="font-serif font-light text-[#ffffff] text-6xl sm:text-7xl lg:text-8xl tracking-[0.12em] mt-6"
            >
              UNSPOKEN<sup className="text-[0.3em] tracking-normal align-super ml-1">&trade;</sup>
            </h1>
            <div className="h-px w-28 mt-6" style={{ backgroundColor: SAGE }} />
          </Reveal>
          <Reveal delay={0.16}>
            <p className="font-serif italic text-[#e4d7da] text-2xl md:text-4xl font-light leading-snug mt-8 max-w-3xl">
              Breaking the silence around men&rsquo;s mental health.
            </p>
          </Reveal>
          <Reveal delay={0.22}>
            <p className="text-[#bfa9af] font-light leading-relaxed mt-6 max-w-2xl text-lg">
              You don&rsquo;t have to carry everything in silence.
            </p>
          </Reveal>
          <Reveal delay={0.28}>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#us-support"
                data-testid="us-support-cta"
                className="group inline-flex items-center gap-3 overline px-8 py-5 text-white rounded-full transition-opacity hover:opacity-90"
                style={{ backgroundColor: SAGE }}
              >
                Support UNSPOKEN <ArrowUpRight size={16} className="hover-arrow" />
              </a>
              <Link
                to="/foundation/contact?intent=partner"
                data-testid="us-partner-cta"
                className="group inline-flex items-center gap-3 overline px-8 py-5 border border-[#efe6e8]/25 text-[#efe6e8] hover:bg-white/10 transition-colors rounded-full"
              >
                Start the Conversation <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-28 border-t border-white/10" data-testid="us-intro">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="font-serif text-[#ffffff] text-3xl md:text-4xl font-light leading-snug">
              Men carry a lot that is never spoken about.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#d6c6ca] font-light leading-[1.9] text-lg mt-8">
              The pressure to provide. The expectation to stay strong. Fatherhood. Relationships. Finances. Work.
              Loss. Stress. Fear. Responsibility. And sometimes simply trying to figure out who you are while
              everyone else depends on you.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-[#d6c6ca] font-light leading-[1.9] text-lg mt-6">
              UNSPOKEN&trade; was created to bring those conversations into the open &mdash; creating space for men to
              connect, decompress, have real conversations, build meaningful relationships, and discover healthy ways
              to care for their mental and emotional well-being.
            </p>
          </Reveal>
          <Reveal delay={0.22}>
            <blockquote className="mt-10 pl-6 border-l-2 font-serif italic text-[#ffffff] text-2xl md:text-3xl font-light leading-snug" style={{ borderColor: SAGE }}>
              Because strength isn&rsquo;t pretending everything is okay. Sometimes strength is finally talking about it.
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* WHAT WE TALK ABOUT */}
      <section className="py-20 md:py-28 border-t border-white/10 bg-white/[0.03]" data-testid="us-pillars">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal><Eyebrow>What We Talk About</Eyebrow></Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-serif font-light text-[#ffffff] text-4xl md:text-5xl mt-4 leading-tight">
              Mental Health. <em className="italic">Brotherhood. Support.</em>
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={0.04 * i}>
                <div className="bg-white/[0.05] border border-white/12 rounded-2xl p-7 h-full shadow-sm transition-shadow hover:shadow-md">
                  <p.icon size={26} strokeWidth={1.5} style={{ color: SAGE }} />
                  <p className="text-[#ffffff] text-lg font-medium mt-5">{p.title}</p>
                  <p className="text-[#bfa9af] text-sm leading-relaxed mt-3 font-light">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THE CONVERSATIONS WE DON'T HAVE ENOUGH */}
      <section className="py-20 md:py-28 border-t border-white/10" data-testid="us-conversations">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <Reveal><Eyebrow>The Conversations We Don't Have Enough</Eyebrow></Reveal>
          <div className="flex flex-wrap gap-3 mt-10">
            {conversations.map((c, i) => (
              <Reveal key={c} delay={0.02 * i}>
                <span className="inline-flex items-center text-sm font-light px-4 py-2 rounded-full bg-white/[0.06] border border-white/12 text-[#e4d7da]">
                  {c}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="text-[#d6c6ca] font-light leading-[1.9] text-lg mt-12 max-w-2xl">
              UNSPOKEN&trade; isn&rsquo;t only about talking. It&rsquo;s about connection, movement, experiences,
              brotherhood, resources, and creating healthier ways for men to navigate life. Some days that&rsquo;s a
              serious conversation. Other days it&rsquo;s working out, fishing, sitting around a fire, taking a trip,
              or simply being around other men who understand.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="font-serif italic text-[#ffffff] text-2xl md:text-3xl font-light mt-8">
              There is no single way to take care of your mental well-being.
            </p>
          </Reveal>
        </div>
      </section>

      {/* JOURNAL MOMENT */}
      <section className="py-20 md:py-28 border-t border-white/10" data-testid="us-journal">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="relative bg-white/[0.05] rounded-3xl border border-white/12 shadow-sm p-10 md:p-16 overflow-hidden">
              <div className="absolute left-0 top-10 bottom-10 w-1 rounded-full" style={{ backgroundColor: SAGE }} aria-hidden="true" />
              <p className="overline text-[#b39aa0]">A quiet check-in</p>
              <p className="font-serif italic text-[#ffffff] text-4xl md:text-5xl font-light leading-tight mt-6">
                &ldquo;How are you <em className="italic">really</em> doing?&rdquo;
              </p>
              <p className="text-[#bfa9af] font-light leading-relaxed mt-8 text-lg">
                No pressure to have all the answers. No expectation to carry everything alone.
              </p>
              <p className="text-[#d6c6ca] font-light leading-relaxed mt-4">
                You don&rsquo;t have to carry everything in silence.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WAYS TO SUPPORT */}
      <section id="us-support" className="py-20 md:py-28 border-t border-white/10 bg-white/[0.03]" data-testid="us-ways">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal><Eyebrow>Support UNSPOKEN&trade;</Eyebrow></Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-serif font-light text-[#ffffff] text-4xl md:text-5xl mt-4 leading-tight">
              Help us <em className="italic">break the silence.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#d6c6ca] font-light leading-relaxed mt-6 max-w-2xl text-lg">
              Your support helps create UNSPOKEN&trade; experiences, wellness activities, retreats, community
              conversations, and opportunities for men to connect with resources and one another.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {waysToSupport.map((w, i) => (
              <Reveal key={w.title} delay={0.04 * i}>
                <div className="bg-white/[0.05] border border-white/12 rounded-2xl p-7 h-full shadow-sm">
                  <w.icon size={24} strokeWidth={1.5} style={{ color: SAGE }} />
                  <p className="text-[#ffffff] text-lg font-medium mt-5">{w.title}</p>
                  <p className="text-[#bfa9af] text-sm leading-relaxed mt-3 font-light">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MOVEMBER FLYER — see & share */}
      <section className="py-20 md:py-28 border-t border-white/10 bg-[#140609]" data-testid="us-flyer">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <div className="relative">
              <div className="pointer-events-none absolute -inset-6 rounded-3xl z-0"
                style={{ background: "radial-gradient(circle, rgba(179,71,90,0.28), transparent 70%)" }} aria-hidden="true" />
              <img src="/flyer/unspoken-flyer.png"
                alt="UNSPOKEN — Movember 2026, Men's Mental Health Awareness Month. 30 Men. 30 Days. One Message: You Matter."
                data-testid="us-flyer-image"
                className="relative z-10 w-full h-auto rounded-lg border border-white/15 shadow-2xl" loading="lazy" />
            </div>
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal><Eyebrow>Movember · November 2026</Eyebrow></Reveal>
            <Reveal delay={0.06}>
              <h2 className="font-serif font-light text-white text-4xl md:text-5xl mt-5 leading-tight tracking-tight">
                Print it. Post it. <span className="italic" style={{ color: SAGE }}>Pass it on.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[#d6c6ca] font-light leading-relaxed mt-6 max-w-xl">
                Throughout <strong className="text-white font-semibold">Movember (Men&rsquo;s Health Awareness Month)</strong> —
                30 Men. 30 Days. One Message: <em className="italic text-white">You Matter.</em> Help us reach the men who
                need it most. Share the flyer to Facebook or Instagram in one tap, or download it to print and hang in
                your gym, barbershop, or storefront.
              </p>
            </Reveal>

            {/* Social share buttons */}
            <Reveal delay={0.14}>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={shareFacebook} data-testid="us-share-facebook"
                  className="group inline-flex items-center gap-2 overline px-6 py-4 text-white transition-opacity hover:opacity-90 rounded-full"
                  style={{ backgroundColor: SAGE }}>
                  <Facebook size={16} /> Share on Facebook
                </button>
                <button onClick={shareInstagram} data-testid="us-share-instagram"
                  className="group inline-flex items-center gap-2 overline px-6 py-4 border border-[#efe6e8]/40 text-white hover:bg-white/10 transition-colors rounded-full">
                  <Instagram size={16} /> Share on Instagram
                </button>
                <button onClick={copyLink} data-testid="us-share-copy"
                  className="group inline-flex items-center gap-2 overline px-6 py-4 border border-white/20 text-white/80 hover:text-white hover:border-white/50 transition-colors rounded-full">
                  <LinkIcon size={15} /> Copy Link
                </button>
              </div>
            </Reveal>

            <Reveal delay={0.18}>
              <div className="mt-6">
                <a href="/flyer/UNSPOKEN_Movember_2026.pdf" target="_blank" rel="noopener noreferrer" data-testid="us-flyer-download"
                  className="group inline-flex items-center gap-3 overline px-7 py-4 bg-white text-black hover:bg-[#f3dde1] transition-colors rounded-full">
                  <Download size={15} /> Download the Flyer (PDF)
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.22}>
              <p className="text-white/40 text-xs font-light mt-4 max-w-md">
                Instagram doesn&rsquo;t allow direct web posting — we&rsquo;ll copy the link and open Instagram so you
                can paste it into your post or story.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PLEDGE WISHLIST */}
      <div className="border-t border-white/10">
        <ExperienceWishlist config={US_WISHLIST_CONFIG} />
      </div>

      {/* CLOSING */}
      <section className="py-24 md:py-32 border-t border-white/10 bg-[#180a0d]" data-testid="us-closing">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <Eyebrow>Connection · Movement · Peace · Brotherhood · Support</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="font-serif italic text-[#ffffff] text-3xl md:text-5xl font-light leading-tight mt-8">
              You don&rsquo;t have to carry everything alone.
            </p>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="text-[#d6c6ca] font-light leading-relaxed mt-8 max-w-xl mx-auto text-lg">
              Help us create more moments where men can just be men.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-12 flex flex-wrap justify-center gap-4">
              <Link
                to="/foundation/donate"
                data-testid="us-closing-donate"
                className="group inline-flex items-center gap-3 overline px-8 py-5 text-white rounded-full transition-opacity hover:opacity-90"
                style={{ backgroundColor: SAGE }}
              >
                Support the Mission <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
              <Link
                to="/foundation/contact?intent=partner"
                data-testid="us-closing-partner"
                className="group inline-flex items-center gap-3 overline px-8 py-5 border border-[#efe6e8]/25 text-[#efe6e8] hover:bg-white/10 transition-colors rounded-full"
              >
                Become a Partner <ArrowUpRight size={16} className="hover-arrow" />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/foundation"
              data-testid="us-back"
              className="group inline-flex items-center gap-2 text-sm text-[#bfa9af] hover:text-[#ffffff] mt-16 transition-colors"
            >
              <ArrowLeft size={14} /> Back to the Foundation
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
